import { rolesManagementApi, subCompaniesApi } from 'app/services/api';
import { normalize, schema } from 'normalizr';

import { catchAsyncActionError } from 'utils/action';
import { createNamedActions } from 'utils/namedActionTypesProxy';

import { selectMasterCompanyId } from 'store/auth/selectors';

export const ROLES_ACTION_TYPE = createNamedActions('roles', {
  GET_ROLES: 'GET_ROLES',
  ADD_ROLE: 'ADD_ROLE',
  EDIT_ROLE: 'EDIT_ROLE',
  DELETE_ROLE: 'DELETE_ROLE',
  DUPLICATE_ROLE: 'DUPLICATE_ROLE',
  MERGE_ROLES: 'MERGE_ROLES',
});

const roles = new schema.Entity(
  'roles',
  {},
  {
    idAttribute: 'id',
    processStrategy: (role, parent) => ({
      ...role,
      priority: parent.findIndex(item => role.id === item.id),
    }),
  },
);

export const getRolesAction = catchAsyncActionError(
  ({ includeDeletedRoles, skipForbiddenError } = {}) =>
    async (dispatch, getState) => {
      const state = getState();
      const companyId = selectMasterCompanyId(state);

      const { data } = await rolesManagementApi.getRoles({
        isDeleted: includeDeletedRoles ? '' : false,
        companyId,
        skipForbiddenError,
      });

      return dispatch({
        type: ROLES_ACTION_TYPE.GET_ROLES,
        payload: normalize(data, [roles]).entities.roles,
      });
    },
);

// A scoped broker is 403'd on the company-wide roles list, so their security groups' names never
// reach the roles store — leaving the claims editor's required Name field empty (blocking save) and
// the header showing "undefined". Hydrate id+name from the broker's own allowed-roles endpoint and
// MERGE (not replace) so it can't clobber claims fetched in parallel.
export const getSubCompanyAllowedRolesMetaAction = catchAsyncActionError(
  ({ subCompanyId }) =>
    async dispatch => {
      const { data } = await subCompaniesApi.getSubCompanyAllowedRolesDetails({ subCompanyId });
      const rolesMeta = (data || []).reduce(
        (acc, { roleId, roleName }) => ({ ...acc, [roleId]: { id: roleId, name: roleName } }),
        {},
      );

      return dispatch({ type: ROLES_ACTION_TYPE.MERGE_ROLES, payload: rolesMeta });
    },
);

export const createRoleAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await rolesManagementApi.createRole(params);

  return dispatch({
    type: ROLES_ACTION_TYPE.ADD_ROLE,
    payload: data,
  });
});

export const editRoleAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await rolesManagementApi.editRole(params);

  return dispatch({
    type: ROLES_ACTION_TYPE.EDIT_ROLE,
    payload: data,
  });
});

export const duplicateRoleAction = catchAsyncActionError(params => async (dispatch, getState) => {
  const state = getState();
  const companyId = selectMasterCompanyId(state);

  const { data } = await rolesManagementApi.copyRole({ ...params, companyId });

  return dispatch({
    type: ROLES_ACTION_TYPE.DUPLICATE_ROLE,
    payload: data,
  });
});

export const deleteRoleAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await rolesManagementApi.deleteRole(params);

  return dispatch({
    type: ROLES_ACTION_TYPE.DELETE_ROLE,
    payload: data,
    meta: params,
  });
});
